import React from "react";
import {
  Bell,
  Check,
  EyeSlash,
  Plus,
  User,
  PenNib,
  Stethoscope,
  CloudArrowUp,
  PushPin,
} from "@phosphor-icons/react";
import "./EmptyDraw.css";

/**
 * Decorative CSS illustrations for empty states.
 * Follows the punch-card ticket design language (PunchCardTab):
 * small tilted card object, mint accent, skeleton lines,
 * straightens + lifts on parent hover.
 *
 * Purely decorative — always rendered with aria-hidden.
 */

const CalendarDraw = () => (
  <div className="ed-card ed-cal">
    <div className="ed-cal-header">
      <span className="ed-cal-ring" />
      <span className="ed-cal-ring" />
    </div>
    <div className="ed-cal-grid">
      {Array.from({ length: 21 }).map((_, i) => (
        <span key={i} className={`ed-cal-cell ${i === 9 ? "today" : ""}`}>
          {i === 9 && <Check size={9} weight="bold" />}
        </span>
      ))}
    </div>
  </div>
);

const FilesDraw = () => (
  <div className="ed-folder">
    <span className="ed-folder-tab" />
    <div className="ed-folder-paper">
      <span className="ed-line w75" />
      <span className="ed-line w55" />
    </div>
    <div className="ed-folder-front">
      <span className="ed-chip">
        <CloudArrowUp size={16} weight="bold" />
      </span>
    </div>
  </div>
);

const TreatmentDraw = () => (
  <div className="ed-card ed-clipboard">
    <span className="ed-clipboard-clip" />
    <div className="ed-clipboard-body">
      <span className="ed-line w75" />
      <span className="ed-line w55" />
      <span className="ed-line w40" />
      <div className="ed-pill-row">
        <span className="ed-pill" />
        <span className="ed-pill" />
        <span className="ed-chip">
          <Stethoscope size={13} weight="bold" />
        </span>
      </div>
    </div>
  </div>
);

const PaymentDraw = () => (
  <div className="ed-receipt">
    <span className="ed-line w75" />
    <span className="ed-line w55" />
    <span className="ed-receipt-divider" />
    <div className="ed-receipt-total">
      <span className="ed-line w40" />
      <span className="ed-chip ed-shekel">₪</span>
    </div>
  </div>
);

const FormDraw = () => (
  <div className="ed-card ed-form">
    <div className="ed-form-body">
      <span className="ed-line w75" />
      <span className="ed-line w55" />
      <span className="ed-line w75" />
      <div className="ed-form-sig">
        <span className="ed-sig-line" />
        <span className="ed-chip">
          <PenNib size={13} weight="bold" />
        </span>
      </div>
    </div>
  </div>
);

const TaskDraw = () => (
  <div className="ed-card ed-tasklist">
    <div className="ed-task-row">
      <span className="ed-box done">
        <Check size={10} weight="bold" />
      </span>
      <span className="ed-line w75" />
    </div>
    <div className="ed-task-row">
      <span className="ed-box" />
      <span className="ed-line w55" />
    </div>
    <div className="ed-task-row">
      <span className="ed-box" />
      <span className="ed-line w40" />
    </div>
  </div>
);

const ReminderDraw = () => (
  <div className="ed-sticky">
    <span className="ed-sticky-pin">
      <PushPin size={14} weight="fill" />
    </span>
    <span className="ed-sticky-line w75" />
    <span className="ed-sticky-line w55" />
    <span className="ed-sticky-line w40" />
    <span className="ed-sticky-fold" />
  </div>
);

const LeadDraw = () => (
  <div className="ed-card ed-lead">
    <div className="ed-avatar">
      <User size={18} weight="fill" />
      <span className="ed-avatar-badge">
        <Plus size={8} weight="bold" />
      </span>
    </div>
    <div className="ed-lead-lines">
      <span className="ed-line w75" />
      <span className="ed-line w55" />
      <span className="ed-pill mint" />
    </div>
  </div>
);

const QuestionnaireDraw = () => (
  <div className="ed-card ed-quest">
    <div className="ed-quest-row">
      <span className="ed-radio" />
      <span className="ed-line w75" />
    </div>
    <div className="ed-quest-row">
      <span className="ed-radio checked" />
      <span className="ed-line w55" />
    </div>
    <div className="ed-quest-row">
      <span className="ed-radio" />
      <span className="ed-line w40" />
    </div>
  </div>
);

const TemplateDraw = () => (
  <div className="ed-card ed-template">
    <span className="ed-tmpl-block" />
    <span className="ed-tmpl-block" />
    <span className="ed-tmpl-block" />
    <span className="ed-tmpl-add">
      <Plus size={14} weight="bold" />
    </span>
  </div>
);

const PatientsDraw = () => (
  <div className="ed-stack">
    <span className="ed-stack-back" />
    <div className="ed-card ed-lead">
      <div className="ed-avatar">
        <User size={18} weight="fill" />
      </div>
      <div className="ed-lead-lines">
        <span className="ed-line w75" />
        <span className="ed-line w55" />
      </div>
    </div>
  </div>
);

const SearchDraw = () => (
  <div className="ed-search">
    <div className="ed-card ed-search-card">
      <span className="ed-line w75" />
      <span className="ed-line w55" />
      <span className="ed-line w40" />
    </div>
    <span className="ed-mag">
      <span className="ed-mag-handle" />
    </span>
  </div>
);

const DoneDraw = () => (
  <div className="ed-card ed-minicard">
    <span className="ed-chip">
      <Check size={14} weight="bold" />
    </span>
    <div className="ed-minicard-lines">
      <span className="ed-line w75" />
      <span className="ed-line w55" />
    </div>
  </div>
);

const BellDraw = () => (
  <div className="ed-card ed-minicard">
    <span className="ed-chip">
      <Bell size={14} weight="fill" />
    </span>
    <div className="ed-minicard-lines">
      <span className="ed-line w75" />
      <span className="ed-line w55" />
    </div>
  </div>
);

const HiddenDraw = () => (
  <div className="ed-sticky">
    <span className="ed-chip">
      <EyeSlash size={14} weight="bold" />
    </span>
    <span className="ed-sticky-line" />
    <span className="ed-sticky-fold" />
  </div>
);

const VARIANTS = {
  calendar: CalendarDraw,
  files: FilesDraw,
  treatment: TreatmentDraw,
  payment: PaymentDraw,
  form: FormDraw,
  task: TaskDraw,
  reminder: ReminderDraw,
  lead: LeadDraw,
  questionnaire: QuestionnaireDraw,
  template: TemplateDraw,
  patients: PatientsDraw,
  search: SearchDraw,
  done: DoneDraw,
  bell: BellDraw,
  hidden: HiddenDraw,
};

const EmptyDraw = ({ variant, size, className = "" }) => {
  const Draw = VARIANTS[variant];
  if (!Draw) return null;
  return (
    <div
      className={`empty-draw ed-${variant} ${size === "sm" ? "ed-sm" : ""} ${className}`}
      aria-hidden="true"
    >
      <Draw />
    </div>
  );
};

export default EmptyDraw;
